import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import { api } from "@/lib/api";
import useSeo from "@/hooks/useSeo";

const PAGE_SIZE = 12; // initial cards shown; "View more" reveals the rest in 9-card chunks
const LOAD_MORE_STEP = 9;

const CATEGORY_GRADIENTS = {
  "Menopause & Identity": "from-sage/15 to-sage/5",
  "Clinical Hypnotherapy": "from-gold/15 to-gold/5",
  "Nervous System": "from-blush/20 to-blush/5",
  "Personal Growth & Reinvention": "from-sage-dark/15 to-sage/5",
  "Life Transitions": "from-gold/10 to-cream-soft",
  "Mindset": "from-sage/10 to-gold/5",
  "Confidence": "from-blush/15 to-gold/5",
  "Emotional Wellbeing": "from-sage/10 to-cream-soft",
};

function gradientFor(category) {
  return CATEGORY_GRADIENTS[category] || "from-cream-soft to-cream";
}

function ArticleCard({ article, featured = false }) {
  return (
    <Link
      to={`/insights/${article.slug}`}
      className="group block"
      data-testid={`insight-card-${article.slug}`}
    >
      <article
        className={`relative h-full rounded-[1.5rem] border border-hairline overflow-hidden bg-white transition-all hover:border-sage/40 hover:shadow-[0_20px_50px_-20px_rgba(74,93,79,0.18)] ${
          featured ? "md:col-span-2" : ""
        }`}
      >
        {article.featured_image ? (
          <div className={`aspect-[16/${featured ? "9" : "10"}] overflow-hidden bg-cream-soft`}>
            <img
              src={article.featured_image}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
          </div>
        ) : (
          <div
            className={`aspect-[16/${featured ? "9" : "10"}] flex items-center justify-center bg-gradient-to-br ${gradientFor(
              article.category
            )} relative overflow-hidden`}
          >
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-0 right-0 w-40 h-40 bg-cream/30 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gold/15 rounded-full blur-2xl" />
            </div>
            <div className="relative text-center px-8">
              <div className="font-serif-display italic text-4xl md:text-5xl text-sage/40 leading-none">
                Pip Bennett
              </div>
              <div className="mt-3 text-[0.65rem] tracking-[0.18em] uppercase text-sage/55 font-medium">
                Insights
              </div>
            </div>
          </div>
        )}
        <div className="p-7 md:p-8">
          <div className="flex items-center gap-3 text-xs">
            <span className="eyebrow text-sage" data-testid={`insight-card-category-${article.slug}`}>
              {article.category}
            </span>
            <span className="text-ink-soft/40">·</span>
            <span className="inline-flex items-center gap-1 text-ink-soft">
              <Clock size={12} strokeWidth={1.8} />
              {article.reading_time_minutes} min
            </span>
          </div>
          <h3
            className={`mt-4 font-serif-display ${
              featured ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"
            } text-ink leading-snug group-hover:text-sage transition-colors`}
          >
            {article.title}
          </h3>
          {article.subtitle && (
            <p className="mt-2 text-sm text-ink-soft italic leading-relaxed">{article.subtitle}</p>
          )}
          <p className="mt-4 text-ink-soft leading-relaxed text-[0.95rem]">{article.excerpt}</p>
          <div className="mt-6 inline-flex items-center gap-1.5 text-sage font-medium text-sm">
            Read the piece
            <ArrowRight size={14} strokeWidth={1.8} className="group-hover:translate-x-1 transition" />
          </div>
        </div>
      </article>
    </Link>
  );
}

export default function Insights() {
  useSeo({
    title: "Insights · Pip Bennett — On menopause, midlife & the work of becoming",
    description:
      "Essays, articles and quiet thinking from Pip Bennett on menopause, midlife, clinical hypnotherapy, nervous system support, and the long slow work of becoming who you really are.",
    canonical: "https://pipbennett.com/insights",
  });

  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    (async () => {
      try {
        const [a, c] = await Promise.all([
          api.get("/insights"),
          api.get("/insights/categories"),
        ]);
        setArticles(a.data || []);
        setCategories(c.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (activeCategory === "All") return articles;
    return articles.filter((a) => a.category === activeCategory);
  }, [articles, activeCategory]);

  const selectCategory = (cat) => {
    setActiveCategory(cat);
    setVisibleCount(PAGE_SIZE); // reset the "View more" window when filter changes
  };

  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  return (
    <div data-testid="page-insights" className="bg-cream">
      {/* HERO — clear page purpose: blog library title, editorial supporting line, short intro */}
      <section className="pt-8 md:pt-12 pb-6 md:pb-8">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow mb-3 text-gold-dark">The Library</div>
            <h1
              className="font-serif-display text-4xl md:text-5xl lg:text-[3.25rem] text-ink leading-[1.08] max-w-3xl"
              data-testid="insights-title"
            >
              The Journal <span className="text-ink-soft/70">by</span>{" "}
              <span className="text-sage">Pip Bennett</span>
            </h1>
            <p className="mt-4 font-serif-display italic text-xl md:text-2xl text-sage/90 leading-snug max-w-2xl">
              The slow, honest work of becoming.
            </p>
            <p className="mt-5 max-w-xl text-base md:text-[1.05rem] text-ink-soft leading-relaxed">
              A growing library of essays and reflections on menopause, midlife, identity, the
              nervous system, personal growth, and what it takes to live a life that feels like
              yours.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* CATEGORY FILTERS — stronger pills, clearer hierarchy, scalable across many topics */}
      {categories.length > 0 && (
        <section
          className="sticky top-20 z-30 bg-cream/92 backdrop-blur-xl border-y border-hairline/60"
          data-testid="insights-filter-rail"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-4 md:py-5">
            <div className="flex items-center gap-3">
              <span className="hidden md:inline-block text-[0.68rem] tracking-[0.2em] uppercase text-ink-soft font-semibold pr-2 whitespace-nowrap">
                Browse&nbsp;by
              </span>
              <div
                className="flex gap-2.5 overflow-x-auto pb-0.5 scrollbar-hide flex-1"
                data-testid="insights-categories"
              >
                <button
                  type="button"
                  onClick={() => selectCategory("All")}
                  className={`whitespace-nowrap text-[0.82rem] font-semibold tracking-wide px-4 py-2 rounded-full border transition ${
                    activeCategory === "All"
                      ? "bg-sage text-cream border-sage shadow-[0_4px_14px_-4px_rgba(74,93,79,0.4)]"
                      : "bg-cream-soft text-ink border-hairline hover:border-sage hover:text-sage hover:bg-cream"
                  }`}
                  data-testid="insights-filter-all"
                >
                  All <span className="opacity-70 font-medium">· {articles.length}</span>
                </button>
                {categories.map((c) => (
                  <button
                    key={c.category}
                    type="button"
                    onClick={() => selectCategory(c.category)}
                    data-testid={`insights-filter-${c.category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`whitespace-nowrap text-[0.82rem] font-semibold tracking-wide px-4 py-2 rounded-full border transition ${
                      activeCategory === c.category
                        ? "bg-sage text-cream border-sage shadow-[0_4px_14px_-4px_rgba(74,93,79,0.4)]"
                        : "bg-cream-soft text-ink border-hairline hover:border-sage hover:text-sage hover:bg-cream"
                    }`}
                  >
                    {c.category} <span className="opacity-70 font-medium">· {c.count}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* GRID — tighter padding so first row sits in the fold on desktop */}
      <section className="pt-8 md:pt-10 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          {loading ? (
            <div className="text-center py-20 text-ink-soft">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-ink-soft">
              No articles yet in this category. New writing is on the way.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {visible.map((article, i) => (
                  <SectionReveal key={article.slug} delay={(i % 3) * 60}>
                    <ArticleCard article={article} />
                  </SectionReveal>
                ))}
              </div>

              {hasMore && (
                <div className="mt-12 md:mt-14 flex flex-col items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setVisibleCount((n) => n + LOAD_MORE_STEP)}
                    className="inline-flex items-center gap-2 bg-transparent hover:bg-sage hover:text-cream text-sage border border-sage rounded-full px-7 py-3 text-sm font-medium transition"
                    data-testid="insights-load-more"
                  >
                    View more pieces
                    <ArrowRight size={14} strokeWidth={1.8} />
                  </button>
                  <span className="text-xs text-ink-soft/70">
                    Showing {visible.length} of {filtered.length}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
